import { CheckCircle2 } from "lucide-react";
import { useLang } from "@/context/LanguageContext";

/**
 * Klarna badge/banner — pill style. `variant` controls layout:
 *  - "hero": large horizontal banner (home)
 *  - "inline": compact for product detail
 *  - "chip": tiny chip
 */
export default function KlarnaBanner({ variant = "hero", amount }) {
  const { t, lang } = useLang();
  const rate = amount ? (amount / 3).toFixed(2) : null;
  const klarnaHref = lang === "en"
    ? "https://www.klarna.com/uk/how-it-works/"
    : lang === "fr"
      ? "https://www.klarna.com/fr/comment-ca-marche/"
      : lang === "de"
        ? "https://www.klarna.com/de/wie-funktioniert-es/"
        : lang === "es"
          ? "https://www.klarna.com/es/como-funciona/"
          : "https://www.klarna.com/it/come-funziona/";

  if (variant === "chip") {
    return (
      <span data-testid="klarna-chip" className="inline-flex items-center gap-1.5 rounded-full bg-[#FFB3C7] text-[#0A0A0A] px-2.5 py-1 text-[11px] font-semibold">
        <KlarnaWord /> {t("klarna_chip_pay3")}
      </span>
    );
  }

  if (variant === "inline") {
    return (
      <div data-testid="klarna-inline" className="flex items-start gap-3 bg-[#FFF5F7] border border-[#FFB3C7]/70 rounded-xl p-4">
        <span className="w-10 h-10 rounded-lg bg-[#FFB3C7] flex items-center justify-center flex-shrink-0">
          <KlarnaWord />
        </span>
        <div className="flex-1 min-w-0 text-sm">
          <div className="font-semibold text-xd-text">
            {t("klarna_pay_prefix")} <span className="font-mono-tech">{t("klarna_pay_3")}</span> {t("klarna_pay_suffix")}
          </div>
          {rate && (
            <div className="text-xd-muted text-xs mt-0.5">
              {t("card_price_from")} <span className="font-mono-tech font-semibold text-xd-text">€ {rate}</span>{t("klarna_per_month")} · {t("klarna_no_fee")}
            </div>
          )}
          <a href={klarnaHref} target="_blank" rel="noreferrer" className="text-[11px] text-[#0A0A0A] hover:underline font-medium mt-1 inline-block">
            {t("klarna_learn_more")} ›
          </a>
        </div>
      </div>
    );
  }

  // hero variant — big horizontal banner
  return (
    <section data-testid="klarna-hero-banner" className="max-w-7xl mx-auto px-4 sm:px-6 -mt-2 mb-6">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-[#FFB3C7] via-[#FFC4D3] to-[#FFB3C7] border border-[#FFB3C7]/60 px-5 py-4 sm:px-8 sm:py-5 flex flex-col sm:flex-row items-center gap-4 sm:gap-6">
        <div className="flex items-center gap-4 flex-1">
          <span className="w-12 h-12 rounded-xl bg-white/90 flex items-center justify-center flex-shrink-0 shadow-sm">
            <KlarnaWord large />
          </span>
          <div>
            <div className="font-display text-lg sm:text-xl font-semibold text-[#0A0A0A]">{t("klarna_hero_title")}</div>
            <div className="text-xs sm:text-sm text-[#0A0A0A]/70 mt-0.5">
              {t("klarna_hero_desc")}
            </div>
          </div>
        </div>
        <div className="hidden sm:flex items-center gap-4 text-[11px] font-medium text-[#0A0A0A]/70 whitespace-nowrap">
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t("klarna_bullet_1")}</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t("klarna_bullet_2")}</span>
          <span className="inline-flex items-center gap-1"><CheckCircle2 className="w-3.5 h-3.5" /> {t("klarna_bullet_3")}</span>
        </div>
        {/* decorative dots */}
        <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-white/20" aria-hidden />
        <div className="absolute -left-8 -bottom-10 w-32 h-32 rounded-full bg-white/15" aria-hidden />
      </div>
    </section>
  );
}

function KlarnaWord({ large = false }) {
  return (
    <span className={`font-black italic tracking-tight text-[#0A0A0A] ${large ? "text-lg" : "text-[13px]"}`} style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
      Klarna.
    </span>
  );
}
